import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  Printer,
  Link2,
  Check,
  ExternalLink,
  FileUp,
  FileWarning,
  FileX,
  History,
  Inbox,
  Info,
  Loader2,
  RotateCcw,
  ScanLine,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuthStore } from '@features/authentication/store/authStore';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { apiClient } from '@shared/api';
import { DocTypeChip, LinkStatusChip } from '../DocChips';
import { VerificationChip } from '../VerificationChip';
import { UpdateFilePanel } from './UpdateFilePanel';
import { SharePublicLinkDialog } from './SharePublicLinkDialog';
import { PdfDocumentViewer } from './PdfDocumentViewer';
import { absoluteShareUrl, copyAudienceHint } from '../../config/families';
import { tabForItem } from '../../config/reviewTabs';
import { documentCaptureService } from '../../services/documentCaptureService';
import { useDocumentCaptureStore } from '../../store/documentCaptureStore';
import {
  DocSupersedeState,
  provisionalVersion,
  versionDisplayState,
  type DocumentVersion,
  type DocumentView,
  type VersionDisplayState,
} from '../../types/documentCapture.types';
import {
  apiErrorInfo,
  DOC_FILE_SOURCE_LABELS,
  formatShortDate,
  formatVersionStamp,
  pluralPages,
} from '../../lib/versionFormat';

/**
 * Viewer content (86d3rrfdk) — the dominant PDF viewer + metadata panel + version history.
 * Shared by the `/d/:docNo` route and the in-page overlay variant. Assumes a ready document (edge
 * states are handled by the wrapper).
 *
 * T7 (86d3uc89n) adds the deliberate update lane: an `Update file` toolbar action opening
 * {@link UpdateFilePanel} in place of the viewer body, a `vN since {date}` chip, per-version
 * download + restore in an upgraded version-history card. Mutations hand the fresh
 * {@link DocumentView} up through `onDocUpdated` — the wrapper owns the document state.
 */
interface DocumentViewerContentProps {
  doc: DocumentView;
  /** Called with the fresh view after an update/restore commits (or a 409 forces a refetch). */
  onDocUpdated?: (doc: DocumentView) => void;
}

function pesos(amount: number | null): string {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount);
}

export function DocumentViewerContent({ doc, onDocUpdated }: DocumentViewerContentProps) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState<number | null>(null);
  // The active version's PDF as an object URL. Fetched as bytes rather than pointed at directly
  // because /file needs the bearer token an <iframe> can't send. Re-fetched when the active version
  // changes (update/restore); `pdfNonce` drives the retry affordance.
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfFailed, setPdfFailed] = useState(false);
  const [pdfNonce, setPdfNonce] = useState(0);

  // Return focus to the Update-file trigger when the panel closes (86d3uf18j S11 / WCAG 2.4.3) —
  // otherwise focus falls to document.body and keyboard/SR users lose their place in the toolbar.
  const updateTriggerRef = useRef<HTMLButtonElement>(null);
  const wasUpdateOpen = useRef(false);
  useEffect(() => {
    if (wasUpdateOpen.current && !updateOpen) updateTriggerRef.current?.focus();
    wasUpdateOpen.current = updateOpen;
  }, [updateOpen]);

  // Subscribe to the VALUE, not the getter fn (86d3uf18j L8 / CLAUDE.md 86d3d1z40): a selector that
  // returns the boolean is tracked by the React Compiler and re-runs on a permission change; reading
  // a stable getter fn in render would serve a stale affordance.
  const canUpload = useAuthStore((s) => s.hasPermission(PERMISSIONS.DOCSCAN_UPLOAD));
  // Public sharing is gated by docscan.share AND a shareable family (INV/DR). The server re-checks
  // both and refuses treasury, so this is UI-gating only (86d3udn5u, T8).
  // T10: the dialog owns rotate + the access trail, so it is gated by docscan.share ALONE — every
  // family has a link now, and hiding it for non-INV/DR would leave those links unrotatable.
  const canShare = useAuthStore((s) => s.hasPermission(PERMISSIONS.DOCSCAN_SHARE));

  const activeVersionRow = doc.versions.find((v) => v.supersedeState === DocSupersedeState.Active);

  // W9 — the single provisional (checking / held / failed) version, if any, + review navigation.
  const navigate = useNavigate();
  const openReview = useDocumentCaptureStore((s) => s.openReview);
  const pendingRow = provisionalVersion(doc.versions);
  const pendingState = pendingRow ? versionDisplayState(pendingRow) : null;

  /**
   * 86d403p4v D3 — a 'failed' provisional row means one of two different things, and the chip used to
   * assert the wrong one. If the row has pages, the file was read perfectly well and it is the
   * INDEPENDENT CHECK that gave up; only a row with no pages is actually unreadable. Same signal as
   * ReplacementReviewPanel, and reliable for the same reason (M6 made the verify handler's park carry
   * the real page count instead of hard-coding 0).
   */
  const pendingCheckFailedOnly = pendingState === 'failed' && (pendingRow?.pageCount ?? 0) > 0;
  const openInReview = useCallback(
    (fileId: number) => {
      const item = doc.openReviews.find((r) => r.fileId === fileId);
      navigate('/documents');
      // Derive the tab rather than hardcoding 'replacements' (86d3w2jrj FE-1): the queue groups by
      // reason-then-kind now, so a hardcoded tab can open one the item was filtered out of, leaving
      // the reviewer on an empty list.
      openReview(item ? tabForItem(item) : 'replacements', item?.id);
    },
    [doc.openReviews, navigate, openReview]
  );

  // Fetch the active PDF as an authed blob → object URL for the embedded viewer. Re-runs on version
  // change (update/restore) so the viewer always shows the current bytes; revokes on cleanup.
  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setPdfUrl(null);
    setPdfFailed(false);
    apiClient
      // download: false — this is the viewer rendering, not the user keeping a copy. Leaving it true
      // is what made every glance look like a download in the audit (86d3uu104).
      .get<Blob>(documentCaptureService.fileUrl(doc.docNumber, doc.activeVersion ?? undefined, false), {
        responseType: 'blob',
      })
      .then((res) => {
        if (cancelled) return;
        // Force the MIME type: a blob typed anything else (or nothing) makes the frame download
        // instead of display, whatever the response headers said.
        url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        setPdfUrl(url);
      })
      .catch(() => {
        if (!cancelled) setPdfFailed(true);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [doc.docNumber, doc.activeVersion, pdfNonce]);

  // ONE link circulates (86d3uu104, T10): the capability URL, which is also what NetSuite points at.
  // `/d/{docNumber}` stays as the in-app route and the address bar, but is never copied or shared —
  // leave a second link style in the copy affordances and the unification is fiction.
  const shareUrl = absoluteShareUrl(doc.shareUrl);

  // Synchronous by construction: the URL is already on the payload, so nothing is awaited between the
  // click and writeText. Safari drops clipboard permission across an await and the copy fails silently.
  const copyLink = useCallback(() => {
    if (!shareUrl) return;
    void navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied', { description: copyAudienceHint(doc.prefix) });
    setTimeout(() => setCopied(false), 1500);
  }, [shareUrl, doc.prefix]);

  /** Re-read the document after a rotate so the payload's `shareUrl` reflects the new token. */
  const refreshDoc = useCallback(async () => {
    try {
      onDocUpdated?.(await documentCaptureService.getDocument(doc.docNumber));
    } catch {
      /* the dialog already reported the outcome; a stale URL self-heals on the next load */
    }
  }, [doc.docNumber, onDocUpdated]);

  // The original PDF needs the bearer token, so fetch it as a blob rather than a plain <a href>.
  // `download` declares INTENT to the server, which is what gets audited for non-treasury families
  // (86d3uu104) — so it must be true here (save / print) and false for the viewer's render fetch.
  const withPdfBlob = useCallback(
    async (use: (url: string) => void, version?: number, download = true) => {
      setBusy(true);
      try {
        const res = await apiClient.get<Blob>(
          documentCaptureService.fileUrl(doc.docNumber, version, download),
          { responseType: 'blob' }
        );
        const url = URL.createObjectURL(res.data);
        use(url);
        // Give the browser a beat to consume the URL before revoking.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch {
        toast.error('Couldn’t load the original PDF.');
      } finally {
        setBusy(false);
      }
    },
    [doc.docNumber]
  );

  /** Version-aware download — `{DOCNO}-v{n}.pdf`, matching the server's own naming. */
  const downloadVersion = useCallback(
    (version: number | null) => {
      const v = version ?? doc.activeVersion;
      const name = `${doc.docNumber}${v != null ? `-v${v}` : ''}.pdf`;
      return withPdfBlob((url) => {
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
        toast.success(name, { description: 'Saved to your Downloads folder.' });
      }, v ?? undefined);
    },
    [withPdfBlob, doc.docNumber, doc.activeVersion]
  );

  const print = useCallback(
    // Pin the active version so Print never serves a stale cached prior version (86d3uf18j M1).
    () => void withPdfBlob((url) => window.open(url, '_blank')?.print(), doc.activeVersion ?? undefined),
    [withPdfBlob, doc.activeVersion]
  );

  /** Restore never rewrites history — the older file comes forward as a NEW current version. */
  const restore = useCallback(
    async (version: number) => {
      if (restoringVersion != null) return;
      setRestoringVersion(version);
      const keptVersion = doc.activeVersion;
      try {
        const view = await documentCaptureService.restoreVersion(doc.docNumber, version, doc.linkEpoch);
        onDocUpdated?.(view);
        toast.success(`v${version} is current again (as v${view.activeVersion})`, {
          description: `${doc.docNumber} — nothing was erased; v${keptVersion} is kept.`,
        });
      } catch (err) {
        const { status, message } = apiErrorInfo(err);
        if (status === 409) {
          toast.warning('This document changed while you were looking at it', {
            description: 'It was refreshed — check the versions and try again.',
          });
          try {
            onDocUpdated?.(await documentCaptureService.getDocument(doc.docNumber));
          } catch {
            /* wrapper keeps the stale view; the toast already asked for a retry */
          }
        } else if (status !== 403) {
          // 403 is toasted globally by the apiClient interceptor.
          toast.error(message ?? 'The restore didn’t go through. Please try again.');
        }
      } finally {
        setRestoringVersion(null);
      }
    },
    [doc.docNumber, doc.linkEpoch, doc.activeVersion, onDocUpdated, restoringVersion]
  );

  // The update panel replaces the viewer body inside the same page (the viewer is a route page,
  // STABLE IDENTITIES (86d3w2jrj FE-3): these were inline arrows. `onDocRefreshed` sits in the
  // update panel's verdict-poll dep array, and this component now re-renders on every stream event
  // for its document — a fresh arrow per render restarted that 5-second interval faster than it
  // could ever fire, leaving the panel stuck on "checking" while the verdict was already recorded.
  const handleDocRefreshed = useCallback(
    (view: DocumentView) => onDocUpdated?.(view),
    [onDocUpdated]
  );
  const closeUpdatePanel = useCallback(() => setUpdateOpen(false), []);

  // not a modal — Esc closes the panel, handled inside it).
  if (updateOpen) {
    return (
      <UpdateFilePanel
        doc={doc}
        onClose={closeUpdatePanel}
        onDocRefreshed={handleDocRefreshed}
        // W9 — the accept keeps the panel OPEN on its acknowledgment card (the verdict resolves in
        // place); only swap the document. The panel closes itself via onClose.
        onCommitted={handleDocRefreshed}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
      {/* ---- canvas ---- */}
      <div className="space-y-2">
        {/* action bar — zoom / page nav now live inside the PDF viewer itself */}
        <div className="flex flex-wrap items-center justify-end gap-1">
          {canShare && (
            <SharePublicLinkDialog
              docNumber={doc.docNumber}
              prefix={doc.prefix}
              linkEpoch={doc.linkEpoch}
              onRotated={() => void refreshDoc()}
            />
          )}
          {canUpload && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  ref={updateTriggerRef}
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5"
                  onClick={() => setUpdateOpen(true)}
                  aria-label="Update file"
                >
                  <FileUp className="h-3.5 w-3.5" aria-hidden="true" />
                  Update file
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-64">
                Download it, edit it in Acrobat (append attachments, sign it), then upload it back as
                the new current version.
              </TooltipContent>
            </Tooltip>
          )}
          <IconButton label="Download PDF" onClick={() => void downloadVersion(null)} disabled={busy}>
            <Download className="h-4 w-4" />
          </IconButton>
          <IconButton label="Print" onClick={print} disabled={busy}>
            <Printer className="h-4 w-4" />
          </IconButton>
          <IconButton
            label={shareUrl ? 'Copy link to this document' : 'No link yet — this document isn’t linked in NetSuite'}
            onClick={copyLink}
            disabled={!shareUrl}
          >
            {copied ? <Check className="h-4 w-4 text-success" /> : <Link2 className="h-4 w-4" />}
          </IconButton>
        </div>

        {/* The browser's own PDF viewer — scroll, zoom, find, print, thumbnails, all native. */}
        {pdfFailed ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-muted/30 py-16">
            <FileWarning className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">
              Couldn’t load this PDF. The document is fine — this was a network problem.
            </p>
            <Button variant="outline" size="sm" onClick={() => setPdfNonce((n) => n + 1)}>
              Try again
            </Button>
          </div>
        ) : (
          <PdfDocumentViewer url={pdfUrl} title={doc.docNumber} />
        )}
      </div>

      {/* ---- metadata ---- */}
      <div className="space-y-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2">
            <DocTypeChip value={doc.prefix} />
            <button
              type="button"
              onClick={copyLink}
              disabled={!shareUrl}
              className="truncate font-mono text-sm text-foreground hover:underline disabled:no-underline disabled:opacity-100"
              title={shareUrl ? 'Copy link to this document' : 'No link yet — this document isn’t linked in NetSuite'}
            >
              {doc.docNumber}
            </button>
          </div>

          <dl className="mt-3 space-y-2 text-sm">
            <Row label="Status" wrap>
              <span className="inline-flex flex-wrap items-center gap-1.5">
                <LinkStatusChip
                  status={doc.linkStatus}
                  manualLinkedAt={doc.manualLinkedAt}
                  manualLinkedByName={doc.manualLinkedByName}
                />
                {doc.activeVersion != null && doc.activeVersion > 1 && activeVersionRow && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 rounded-full bg-info/12 px-2 py-0.5 text-xs font-semibold text-info">
                        <FileUp className="h-3 w-3" aria-hidden="true" />
                        v{doc.activeVersion} since {formatShortDate(activeVersionRow.uploadedAt)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-64">
                      An updated file was uploaded for this document. The NetSuite link is unchanged — it
                      serves this version.
                    </TooltipContent>
                  </Tooltip>
                )}
                {/* W9 — dashed provisional chip while an uploaded version is not being served. */}
                {pendingRow && pendingState && pendingState !== 'active' && pendingState !== 'history' && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-xs font-semibold',
                          pendingState === 'checking' && 'border-info/50 bg-info/8 text-info',
                          pendingState === 'held' && 'border-warning/50 bg-warning/8 text-warning',
                          pendingState === 'failed' && 'border-destructive/50 bg-destructive/8 text-destructive'
                        )}
                      >
                        {pendingState === 'checking' ? (
                          <ScanLine className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                        ) : pendingState === 'held' ? (
                          <Inbox className="h-3 w-3" aria-hidden="true" />
                        ) : (
                          <FileX className="h-3 w-3" aria-hidden="true" />
                        )}
                        v{pendingRow.version}{' '}
                        {pendingState === 'checking'
                          ? 'checking'
                          : pendingState === 'held'
                            ? 'in review'
                            : pendingCheckFailedOnly
                              ? 'not checked'
                              : 'unreadable'}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-64">
                      {pendingState === 'checking'
                        ? `A new file was uploaded and is being read. v${doc.activeVersion ?? '—'} is still the version everyone sees.`
                        : pendingState === 'held'
                          ? `An uploaded file couldn't be verified against this document, so a reviewer decides. v${doc.activeVersion ?? '—'} is still being served.`
                          : pendingCheckFailedOnly
                            ? `The uploaded file was read fine, but the independent check couldn't be completed, so nothing confirmed it. Nothing changed — v${doc.activeVersion ?? '—'} is still being served.`
                            : `The uploaded file couldn't be read after several attempts. Nothing changed — v${doc.activeVersion ?? '—'} is still being served.`}
                    </TooltipContent>
                  </Tooltip>
                )}
                {/*
                  86d3x48q7 T13 — verification, the THIRD status axis. Sits AFTER the link-status and
                  version chips and never replaces either: a scan can be linked but unconfirmed, or
                  confirmed but unlinked.

                  HIDDEN when null, which is every document captured before the second reader shipped.
                  Rendering "Not checked" for those would claim the AI call failed — a different
                  problem needing a different fix — and would put a false chip on years of history.
                */}
                {doc.verification && <VerificationChip state={doc.verification} />}
              </span>
            </Row>
            <Row label="Entity">{doc.entityName ?? '—'}</Row>
            <Row label="Transaction date">{doc.tranDate ?? '—'}</Row>
            <Row label="Amount"><span className="tabular-nums">{pesos(doc.amount)}</span></Row>
            {doc.nsInternalId && (
              <Row label="NetSuite">
                {doc.netSuiteUrl ? (
                  // 86d3rrfee — server-assembled deep-link; opens the linked transaction in the ERP.
                  <a
                    href={doc.netSuiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                    title="Open the linked record in NetSuite"
                  >
                    <span className="font-mono text-xs">{doc.nsRecordType} · {doc.nsInternalId}</span>
                    <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span className="sr-only">Open in NetSuite</span>
                  </a>
                ) : (
                  // Linked but the record type has no known UI page — show the reference so it's still findable.
                  <span className="font-mono text-xs">{doc.nsRecordType} · {doc.nsInternalId}</span>
                )}
              </Row>
            )}
          </dl>
        </div>

        {/* version history */}
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="mb-2 flex items-center gap-1.5">
            <h3 className="text-sm font-semibold text-foreground">Version history</h3>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info
                  className="h-3 w-3 text-muted-foreground"
                  aria-label="About version history"
                  tabIndex={0}
                />
              </TooltipTrigger>
              <TooltipContent className="max-w-64">
                Every download, upload and restore is stamped with who did it and when. Older files are
                kept, never overwritten.
              </TooltipContent>
            </Tooltip>
          </div>
          <ul>
            {doc.versions
              .filter((v) => v.version != null)
              .sort((a, b) => (b.version ?? 0) - (a.version ?? 0))
              .map((v) => (
                <VersionRow
                  key={v.fileId}
                  version={v}
                  canUpload={canUpload}
                  busy={busy}
                  restoring={restoringVersion === v.version}
                  anyRestoreRunning={restoringVersion != null}
                  onDownload={() => void downloadVersion(v.version)}
                  onRestore={() => void restore(v.version!)}
                  onOpenReview={() => openInReview(v.fileId)}
                />
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/** W9 pill + meta-tail copy per provisional state — a row that exists but is not served must be
 * impossible to mistake for the served version. */
const PROVISIONAL_ROW: Record<
  Extract<VersionDisplayState, 'checking' | 'held' | 'failed'>,
  { pill: string; tail: string; tone: string; pillTone: string }
> = {
  checking: {
    pill: 'CHECKING',
    tail: 'being read — not served yet',
    tone: 'border-info/55 text-info',
    pillTone: 'bg-info/14 text-info',
  },
  held: {
    pill: 'IN REVIEW',
    tail: 'parked for a reviewer — not served',
    tone: 'border-warning/55 text-warning',
    pillTone: 'bg-warning/14 text-warning',
  },
  failed: {
    pill: 'UNREADABLE',
    tail: "couldn't be read — nothing changed",
    tone: 'border-destructive/55 text-destructive',
    pillTone: 'bg-destructive/14 text-destructive',
  },
};

function VersionRow({
  version: v,
  canUpload,
  busy,
  restoring,
  anyRestoreRunning,
  onDownload,
  onRestore,
  onOpenReview,
}: {
  version: DocumentVersion;
  canUpload: boolean;
  busy: boolean;
  restoring: boolean;
  anyRestoreRunning: boolean;
  onDownload: () => void;
  onRestore: () => void;
  onOpenReview: () => void;
}) {
  const state = versionDisplayState(v);
  const isActive = state === 'active';
  const isHistorical = state === 'history';
  const provisional = state === 'checking' || state === 'held' || state === 'failed';
  const spec = provisional ? PROVISIONAL_ROW[state as 'checking' | 'held' | 'failed'] : null;

  return (
    <li
      className={cn(
        'flex items-start gap-2 border-t border-border/60 py-2 first:border-t-0 first:pt-0',
        provisional && 'opacity-90'
      )}
    >
      <div
        className={cn(
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
          isActive && 'bg-success/12 text-success',
          isHistorical && 'bg-muted text-muted-foreground',
          provisional && spec && cn('border border-dashed bg-transparent', spec.tone)
        )}
        aria-hidden="true"
      >
        {isActive ? (
          <Check className="h-3.5 w-3.5" />
        ) : state === 'checking' ? (
          <ScanLine className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
        ) : state === 'held' ? (
          <Inbox className="h-3.5 w-3.5" />
        ) : state === 'failed' ? (
          <FileX className="h-3.5 w-3.5" />
        ) : (
          <History className="h-3.5 w-3.5" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-foreground">v{v.version}</span>
          {isActive && (
            <span className="text-[10px] font-bold tracking-wide text-success">CURRENT</span>
          )}
          {spec && (
            <span
              className={cn(
                'whitespace-nowrap rounded-full px-1.5 py-0.5 text-[9px] font-bold tracking-wide',
                spec.pillTone
              )}
            >
              {spec.pill}
            </span>
          )}
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {DOC_FILE_SOURCE_LABELS[v.source]} · {formatVersionStamp(v.uploadedAt)} ·{' '}
          {v.uploadedByName ?? `user #${v.uploadedById}`} ·{' '}
          <span className="tabular-nums">{pluralPages(v.pageCount)}</span>
          {spec && <> · {spec.tail}</>}
        </p>
        {v.note && <p className="mt-0.5 text-xs text-foreground">“{v.note}”</p>}
        {/* Held/failed rows get the review link instead of download/restore. */}
        {(state === 'held' || state === 'failed') && (
          <button
            type="button"
            onClick={onOpenReview}
            className={cn(
              'mt-0.5 whitespace-nowrap text-xs font-semibold hover:underline',
              state === 'held' ? 'text-warning' : 'text-destructive'
            )}
          >
            Open in Review →
          </button>
        )}
      </div>

      {/* Provisional rows: no download, no restore — nothing here is servable yet. */}
      {!provisional && (
        <div className="flex shrink-0 gap-1">
          <IconButton label="Download this exact version" onClick={onDownload} disabled={busy} small>
            <Download className="h-3 w-3" />
          </IconButton>
          {isHistorical && canUpload && (
            <IconButton
              label="Make this file current again — kept as a new version, nothing is erased"
              onClick={onRestore}
              disabled={anyRestoreRunning}
              small
            >
              {restoring ? (
                <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" />
              ) : (
                <RotateCcw className="h-3 w-3" />
              )}
            </IconButton>
          )}
        </div>
      )}
    </li>
  );
}

function Row({
  label,
  children,
  wrap = false,
}: {
  label: string;
  children: React.ReactNode;
  /**
   * 86d3yeh4z — let the value WRAP instead of being clipped. `truncate` is right for a long entity
   * name, and wrong for the chip row: chips are `whitespace-nowrap` inside a `flex-wrap` container,
   * so the ellipsis never appears — the row simply gets cut off mid-chip. That was survivable while
   * the longest label was "Not checked" and stopped being so at "Only the barcode read it".
   */
  wrap?: boolean;
}) {
  return (
    <div className={cn('flex gap-3', wrap ? 'items-start justify-between' : 'items-center justify-between')}>
      <dt className="flex-shrink-0 text-muted-foreground">{label}</dt>
      <dd className={cn('min-w-0 text-right text-foreground', wrap ? 'break-words' : 'truncate')}>
        {children}
      </dd>
    </div>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  small,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  small?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={small ? 'h-6 w-6' : 'h-8 w-8'}
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent className="max-w-64">{label}</TooltipContent>
    </Tooltip>
  );
}
